'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet'
import { HandwritingCanvas } from './handwriting-canvas'
import { IconEraser, IconArrowBackUp } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

interface HandwritingSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectCharacter: (char: string) => void
}

export function HandwritingSheet({
  open,
  onOpenChange,
  onSelectCharacter
}: HandwritingSheetProps) {
  const t = useTranslations('search')
  const [predictions, setPredictions] = useState<string[]>([])
  const [isDesktop, setIsDesktop] = useState(false)

  // Detect screen size for responsive behavior
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768)
    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])

  const { canvas, clear, undo, strokeCount, isInitialized } = HandwritingCanvas(
    {
      onPredictions: setPredictions,
      width: 256,
      height: 256,
      className:
        'rounded-lg border cursor-crosshair w-full aspect-square max-w-[256px]'
    }
  )

  const handleSelectChar = (char: string) => {
    onSelectCharacter(char)
    clear()
  }

  // Clear predictions when dialog closes
  useEffect(() => {
    if (!open) {
      setPredictions([])
    }
  }, [open])

  const content = (
    <div className="flex flex-col items-center gap-4">
      {/* Canvas */}
      <div className="relative">
        {canvas}
        {!isInitialized && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
            <span className="text-muted-foreground text-sm">Loading...</span>
          </div>
        )}
      </div>

      {/* Canvas controls */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={undo}
          disabled={strokeCount === 0}
        >
          <IconArrowBackUp className="mr-1.5 h-4 w-4" />
          {t('undo')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={clear}
          disabled={strokeCount === 0}
        >
          <IconEraser className="mr-1.5 h-4 w-4" />
          {t('clearCanvas')}
        </Button>
      </div>

      {/* Predictions - fixed height to prevent layout shift */}
      <div className="flex min-h-[56px] w-full items-center justify-center">
        {predictions.length > 0 ? (
          <div
            className={cn(
              'grid w-full gap-2',
              isDesktop ? 'grid-cols-8' : 'grid-cols-4'
            )}
          >
            {predictions.map((char, i) => (
              <Button
                key={`${char}-${i}`}
                variant={i === 0 ? 'default' : 'outline'}
                className="h-12 text-xl font-medium"
                onClick={() => handleSelectChar(char)}
              >
                {char}
              </Button>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center text-sm">
            {t('noPredictions')}
          </p>
        )}
      </div>
    </div>
  )

  // Desktop: use Dialog
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('handwritingTitle')}</DialogTitle>
            <DialogDescription>{t('handwritingDescription')}</DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    )
  }

  // Mobile: use Sheet
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto max-h-[85vh]">
        <SheetHeader>
          <SheetTitle>{t('handwritingTitle')}</SheetTitle>
          <SheetDescription>{t('handwritingDescription')}</SheetDescription>
        </SheetHeader>
        <div className="p-4">{content}</div>
      </SheetContent>
    </Sheet>
  )
}
